import React from "react";
import { SelectOption } from "@showitinc/showit-wc";

import When from "../../../../components/utils/When";
import AppConstants from "../../../../constants/AppConstants";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import {
  LINE_COLOR,
  LINE_DASH_SPACING,
  LINE_DASH_WIDTH,
  LINE_END_CAP,
  LINE_STYLE,
  LINE_THICKNESS,
} from "../../../../constants/MergeConstants";
import PropertyMaps from "../../../../constants/PropertyMaps";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import Input from "../../../base/Input";
import { COLOR_PICKER, SELECT } from "../../../../constants/InputConstants.mjs";
import { prepareInputUtils } from "../../../../utils/InputUtils";
import CustomizeEffects from "../../shared/CustomizeEffects";

const { LINE_PROPS } = PropertyMaps;

const ElementPropertiesLine = ({
  onPropUpdate,
  element,
  layout,
  isLocked,
  preparedElement,
  mappedElement,
}) => {
  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout
  );

  const elemProps = gatherProps(preparedElement, layout, [...LINE_PROPS]);

  const { onChange } = initFormatter(onPropUpdate, getPropName);

  const separatePath = (path) => path.split(".");
  const getValue = (path) => elemProps.getIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];

  const { canMerge, prepareValue, isSyncMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  return (
    <>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(LINE_STYLE)}
        sync={isSynced(LINE_STYLE)}
        toggleSync={() => toggleSyncedProp(LINE_STYLE)}
      >
        <Input
          context={canMerge(LINE_STYLE)}
          disabled={isLocked}
          inputType={SELECT}
          label="Line Style"
          shift="left"
          onSISelect={onChange(...prepChange(LINE_STYLE))}
          value={prepareValue(LINE_STYLE)}
        >
          <SelectOption value="solid" key="solid">
            Solid
          </SelectOption>
          <SelectOption value="dash" key="dash">
            Dashes
          </SelectOption>
          <SelectOption value="dotted" key="dotted">
            Dots
          </SelectOption>
        </Input>
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(LINE_COLOR)}
        sync={isSynced(LINE_COLOR)}
        toggleSync={() => toggleSyncedProp(LINE_COLOR)}
      >
        <Input
          context={canMerge(LINE_COLOR)}
          color={prepareValue(LINE_COLOR)}
          disabled={isLocked}
          inputType={COLOR_PICKER}
          label="Color"
          onSIChange={onChange(...prepChange(LINE_COLOR))}
          onSIBeforeChange={onChange(...prepChange(LINE_COLOR))}
          onSIInput={onChange(...prepChange(LINE_COLOR), false)}
        />
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(LINE_THICKNESS)}
        sync={isSynced(LINE_THICKNESS)}
        toggleSync={() => toggleSyncedProp(LINE_THICKNESS)}
      >
        <Input
          context={canMerge(LINE_THICKNESS)}
          disabled={isLocked}
          label={
            elemProps.get(LINE_STYLE) === "dotted"
              ? "Dot Size"
              : "Line Thickness"
          }
          min="1"
          onSIBeforeChange={onChange(...prepChange(LINE_THICKNESS))}
          onSIInput={onChange(...prepChange(LINE_THICKNESS), false)}
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(
            LINE_THICKNESS,
            AppConstants.DEFAULT_LINE_PROPERTIES
          )}
        />
      </SplitMergeWrapper>

      <When it={elemProps.get(LINE_STYLE) === "solid"}>
        <SplitMergeWrapper
          layout={layout}
          mixed={isSyncMixed(LINE_END_CAP)}
          sync={isSynced(LINE_END_CAP)}
          toggleSync={() => toggleSyncedProp(LINE_END_CAP)}
        >
          <Input
            context={canMerge(LINE_END_CAP)}
            disabled={isLocked}
            inputType={SELECT}
            label="End Cap"
            onSISelect={onChange(...prepChange(LINE_END_CAP))}
            shift="left"
            value={prepareValue(LINE_END_CAP)}
          >
            <SelectOption value="butt">None</SelectOption>
            <SelectOption value="round">Rounded</SelectOption>
          </Input>
        </SplitMergeWrapper>
      </When>
      <When it={elemProps.get(LINE_STYLE) === "dash"}>
        <SplitMergeWrapper
          layout={layout}
          mixed={isSyncMixed(LINE_DASH_WIDTH)}
          sync={isSynced(LINE_DASH_WIDTH)}
          toggleSync={() => toggleSyncedProp(LINE_DASH_WIDTH)}
        >
          <Input
            context={canMerge(LINE_DASH_WIDTH)}
            disabled={isLocked}
            label="Dash Width"
            min="1"
            max="99"
            onSIBeforeChange={onChange(...prepChange(LINE_DASH_WIDTH))}
            onSIInput={onChange(...prepChange(LINE_DASH_WIDTH), false)}
            type={INPUT_TYPE_NUMBER}
            value={prepareValue(LINE_DASH_WIDTH)}
          />
        </SplitMergeWrapper>
      </When>
      <When it={["dash", "dotted"].includes(elemProps.get(LINE_STYLE))}>
        <SplitMergeWrapper
          layout={layout}
          mixed={isSyncMixed(LINE_DASH_SPACING)}
          sync={isSynced(LINE_DASH_SPACING)}
          toggleSync={() => toggleSyncedProp(LINE_DASH_SPACING)}
        >
          <Input
            context={canMerge(LINE_STYLE)}
            disabled={isLocked}
            label={`${
              getValue(LINE_STYLE) === "dash" ? "Dash" : "Dot"
            } Spacing`}
            min="1"
            onSIBeforeChange={onChange(...prepChange(LINE_DASH_SPACING))}
            onSIInput={onChange(...prepChange(LINE_DASH_SPACING), false)}
            type={INPUT_TYPE_NUMBER}
            value={prepareValue(LINE_DASH_SPACING)}
          />
        </SplitMergeWrapper>
      </When>
      <CustomizeEffects
        mappedElement={mappedElement}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={preparedElement}
      />
    </>
  );
};

export default ElementPropertiesLine;
